import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError, avatarUrl } from "@/lib/api";
import { Btn, Card, Label, Input, Textarea, Select, RoleBadge, ServiceChips } from "@/components/shared";
import { DocumentManager } from "@/components/DocumentManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CONTRACTOR_SERVICES, ONBOARDING_RESPONSES_URL, SUBCONTRACTOR_AGREEMENT_URL, onboardingStatus } from "@/lib/constants";
import { Users, UserPlus, Power, PowerOff, Pencil, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Trash2, MapPin, Wrench, ClipboardCheck, ExternalLink, Megaphone, Send, Search, Map, Rocket, Eye, KeyRound, UserCog, Share2, Plus, X, Globe2 } from "lucide-react";
import { CoverageLeaflet } from "@/components/CoverageLeaflet";
import { LaunchEmailDialog } from "@/components/LaunchEmailDialog";
import { MediaManager } from "@/components/MediaManager";
import { CrewProfileDialog } from "@/components/CrewProfileDialog";

const emptyEdit = { services: [], service_zips: [], service_counties: [] };

export default function Crew() {
  const { t } = useTranslation();
  const [crew, setCrew] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [viewId, setViewId] = useState(null);
  const sendReset = async (c) => {
    if (!window.confirm(`Email a password reset link to ${c.email}?`)) return;
    try { const { data } = await api.post(`/admin/users/${c.id}/send-reset`); toast.success(`Reset link sent to ${data.email} (valid 60 min)`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const convertToClient = async (c) => {
    if (!window.confirm(`Convert ${c.name} from Contractor to Client? They'll be notified by email and moved to your Clients list.`)) return;
    try { await api.post(`/admin/users/${c.id}/convert-role`, { to_role: "client", notify: true }); toast.success(`${c.name} is now a Client`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState("all");
  const [mapIds, setMapIds] = useState([]);
  const toggleMap = (id) => setMapIds((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  const [showMap, setShowMap] = useState(false);
  const [bcastOpen, setBcastOpen] = useState(false);
  const [bSel, setBSel] = useState([]);
  const [bSubject, setBSubject] = useState("");
  const [bMsg, setBMsg] = useState("");
  const [bEmail, setBEmail] = useState(true);
  const [bMedia, setBMedia] = useState([]);
  const [bState, setBState] = useState("all");
  const [bSending, setBSending] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [opStates, setOpStates] = useState([]);
  const [stateFilter, setStateFilter] = useState("all");
  const [coverage, setCoverage] = useState(null);
  const [statesOpen, setStatesOpen] = useState(false);
  const [newState, setNewState] = useState({ code: "", name: "", color: "#22C55E" });

  const load = useCallback(() => api.get("/crew").then((r) => setCrew(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const loadStates = useCallback(() => api.get("/op-states").then((r) => setOpStates(r.data)).catch(() => {}), []);
  useEffect(() => { loadStates(); }, [loadStates]);
  useEffect(() => { if (showMap && !coverage) api.get("/coverage-map").then((r) => setCoverage(r.data)).catch(() => {}); }, [showMap, coverage]);

  const addState = async () => {
    if (!newState.code.trim()) { toast.error("State code required"); return; }
    try { await api.post("/op-states", newState); setNewState({ code: "", name: "", color: "#22C55E" }); loadStates(); toast.success("State added"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delState = async (code) => { if (!window.confirm(`Remove ${code}?`)) return; try { await api.delete(`/op-states/${code}`); loadStates(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const shareCoverage = () => { const url = `${window.location.origin}/coverage/${coverage?.share_token}`; navigator.clipboard?.writeText(url); toast.success("Coverage link copied — share with clients"); };

  const openBcast = () => { setBState(stateFilter); const ids = crew.filter((c) => stateFilter === "all" || c.state === stateFilter).map((c) => c.id); setBSel(ids); setBSubject(""); setBMsg(""); setBEmail(true); setBMedia([]); setBcastOpen(true); };
  const toggleSel = (id) => setBSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bcastStates = [...new Set(crew.map((c) => c.state).filter(Boolean))].sort();
  const bcastCrew = crew.filter((c) => bState === "all" || c.state === bState);
  const allSelected = bcastCrew.length > 0 && bcastCrew.every((c) => bSel.includes(c.id));
  const sendBcast = async () => {
    if (!bSubject.trim()) { toast.error("Add a subject"); return; }
    if (bSel.length === 0) { toast.error("Select at least one contractor"); return; }
    if (bEmail && !window.confirm(`Email this announcement to ${bSel.length} contractor${bSel.length === 1 ? "" : "s"} now? This sends real emails.`)) return;
    setBSending(true);
    try {
      const { data } = await api.post("/crew/broadcast", { subject: bSubject, message: bMsg, contractor_ids: bSel, send_email: bEmail, attachment_ids: bMedia.map((m) => m.id), state: bState === "all" ? null : bState });
      toast.success(data.queued
        ? `Posted to ${data.recipients} contractor(s) · emailing ${data.emailable} now — sends roll out over the next minute`
        : `Posted to ${data.recipients} contractor(s)`);
      setBcastOpen(false); setBMedia([]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBSending(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/crew/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "contractor_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const doImport = async () => {
    if (!file) { toast.error("Choose a .csv or .xlsx file first"); return; }
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/crew/import", fd);
      setResult(data);
      toast.success(`${data.created} added · ${data.updated} updated · ${data.skipped} skipped`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const closeImport = () => { setImportOpen(false); setFile(null); setResult(null); };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/crew", form);
      toast.success("Contractor added");
      setAddOpen(false); setForm({ name: "", email: "", password: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/crew/${c.id}`, { active: !(c.active !== false) });
      toast.success((c.active !== false) ? "Contractor deactivated" : "Contractor reactivated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (c) => setEdit({
    id: c.id, name: c.name || "", email: c.email || "", phone: c.phone || "", company: c.company || "", address: c.address || "",
    residence_zip: c.residence_zip || "", service_radius: c.service_radius || 60, available: !!c.available,
    state: c.state || "",
    service_zips: c.service_zips || [], service_counties: c.service_counties || [],
    services: c.services || [], admin_notes: c.admin_notes || "",
    onboarding_completed: !!c.onboarding_completed, agreement_signed: !!c.agreement_signed,
    bio: c.bio || "", picture_file_id: c.picture_file_id || null, picture: c.picture || null,
  });

  const uploadEditPic = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !edit) return;
    setUploadingPic(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd);
      setEdit((x) => ({ ...x, picture_file_id: up.data.id }));
      toast.success("Photo set");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPic(false); }
  };

  const toArr = (s) => (Array.isArray(s) ? s : String(s).split(",").map((x) => x.trim()).filter(Boolean));

  const saveEdit = async () => {
    try {
      await api.put(`/crew/${edit.id}`, {
        name: edit.name, email: edit.email || undefined, phone: edit.phone, company: edit.company, address: edit.address,
        residence_zip: edit.residence_zip, service_radius: Number(edit.service_radius), available: edit.available, state: edit.state || "",
        service_zips: toArr(edit.service_zips), service_counties: toArr(edit.service_counties),
        services: edit.services, admin_notes: edit.admin_notes, onboarding_completed: edit.onboarding_completed, agreement_signed: edit.agreement_signed,
        bio: edit.bio, picture_file_id: edit.picture_file_id || undefined,
      });
      toast.success("Contractor updated"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/crew/${confirmDel.id}`);
      toast.success("Contractor deleted"); setConfirmDel(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = crew.filter((c) => {
    if (avail === "available" && !c.available) return false;
    if (stateFilter !== "all" && c.state !== stateFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [c.name, c.email, c.company, c.phone, c.address, c.residence_zip,
      (c.service_zips || []).join(" "), (c.service_counties || []).join(" "), (c.services || []).join(" ")]
      .filter(Boolean).join(" ").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{t("crewPage.eyebrow")}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{t("crewPage.title")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={ONBOARDING_RESPONSES_URL} target="_blank" rel="noreferrer" data-testid="onboarding-responses-link"><Btn variant="ghost"><ClipboardCheck size={16} /> Onboarding Responses</Btn></a>
          <a href={SUBCONTRACTOR_AGREEMENT_URL} target="_blank" rel="noreferrer" data-testid="agreement-link"><Btn variant="ghost"><ExternalLink size={16} /> Agreement</Btn></a>
          <Btn variant="secondary" data-testid="launch-email-btn" onClick={() => setLaunchOpen(true)}><Rocket size={16} /> {t("crewPage.launch")}</Btn>
          <Btn variant="secondary" data-testid="email-contractors-btn" onClick={openBcast}><Megaphone size={16} /> {t("crewPage.email")}</Btn>
          <Btn variant="secondary" data-testid="import-crew-btn" onClick={() => setImportOpen(true)}><Upload size={16} /> {t("crewPage.import")}</Btn>
          <Btn data-testid="add-crew-btn" onClick={() => setAddOpen(true)}><UserPlus size={16} /> {t("crewPage.add")}</Btn>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input data-testid="crew-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("crewPage.search")} className="pl-9" />
        </div>
        <div className="flex border-2 border-zinc-800 shrink-0" data-testid="crew-avail-filter">
          <button data-testid="filter-all" onClick={() => setAvail("all")} className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wide ${avail === "all" ? "bg-[#FF5722] text-black" : "text-zinc-400 hover:text-white"}`}>{t("crewPage.all")}</button>
          <button data-testid="filter-available" onClick={() => setAvail("available")} className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wide ${avail === "available" ? "bg-[#FF5722] text-black" : "text-zinc-400 hover:text-white"}`}>{t("crewPage.available")}</button>
        </div>
        <div className="flex border-2 border-zinc-800 shrink-0 flex-wrap" data-testid="crew-state-filter">
          <button data-testid="state-filter-all" onClick={() => setStateFilter("all")} className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wide ${stateFilter === "all" ? "bg-[#FF5722] text-black" : "text-zinc-400 hover:text-white"}`}>All states</button>
          {opStates.map((s) => (
            <button key={s.code} data-testid={`state-filter-${s.code}`} onClick={() => setStateFilter(s.code)} className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wide flex items-center gap-1.5 ${stateFilter === s.code ? "bg-[#FF5722] text-black" : "text-zinc-400 hover:text-white"}`}><span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.code}</button>
          ))}
        </div>
        <Btn variant="ghost" data-testid="manage-states-btn" onClick={() => setStatesOpen(true)}><Globe2 size={16} /> States</Btn>
        <Btn variant="secondary" data-testid="toggle-map-btn" onClick={() => setShowMap((s) => !s)}><Map size={16} /> {showMap ? t("crewPage.hideMap") : t("crewPage.coverageMap")}</Btn>
      </div>

      {showMap && (
        <Card className="p-0 overflow-hidden" data-testid="coverage-map">
          <div className="p-3 border-b-2 border-zinc-800 flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">Coverage Area — {coverage?.points?.length || 0} active contractor zones</span>
            <Btn variant="ghost" data-testid="share-coverage-btn" onClick={shareCoverage}><Share2 size={14} /> Share with clients</Btn>
          </div>
          {coverage ? <CoverageLeaflet points={coverage.points} states={coverage.states} height={480} showNames /> : <div className="h-[300px] flex items-center justify-center text-zinc-600 font-mono">Loading coverage…</div>}
        </Card>
      )}

      {crew.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono"><Users className="inline mr-2" size={16} />{t("crewPage.none")}</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono" data-testid="crew-no-match"><Search className="inline mr-2" size={16} />No contractors match “{q}”.</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 [&>*]:min-w-0">
          {filtered.map((c, i) => {
            const active = c.active !== false;
            const ob = onboardingStatus(c.created_at, c.onboarding_completed);
            return (
              <Card key={c.id} data-testid={`crew-${c.id}`} className={`p-5 animate-rise ${active ? "" : "opacity-60"}`} style={{ animationDelay: `${i * 40}ms` }}>
                <div className="flex items-center gap-4">
                  {avatarUrl(c) ? (
                    <img src={avatarUrl(c)} alt="" className="w-12 h-12 object-cover border-2 border-black shadow-[3px_3px_0px_#000] shrink-0" />
                  ) : (
                    <div className={`w-12 h-12 border-2 border-black shadow-[3px_3px_0px_#000] flex items-center justify-center font-head font-black text-white text-lg shrink-0 ${active ? "bg-blue-600" : "bg-zinc-700"}`}>{c.name?.[0]?.toUpperCase() || "?"}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-head font-bold text-white truncate">{c.name}</div>
                    {c.company && <div className="font-mono text-[11px] text-zinc-400 truncate">{c.company}</div>}
                    <div className="font-mono text-[11px] text-zinc-500 truncate">{c.email}</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <RoleBadge role={c.role} />
                      {c.available && <span className="font-mono text-[9px] uppercase text-[#FF5722] border border-[#FF5722]/50 px-1">Available</span>}
                      {!active && <span className="font-mono text-[9px] uppercase text-red-400 border border-red-900 px-1">Inactive</span>}
                      {ob.done
                        ? <span data-testid={`onboard-badge-${c.id}`} className="font-mono text-[9px] uppercase text-emerald-400 border border-emerald-800 px-1">Form ✓</span>
                        : <span data-testid={`onboard-badge-${c.id}`} className={`font-mono text-[9px] uppercase px-1 border ${ob.overdue ? "text-red-400 border-red-800" : "text-yellow-400 border-yellow-800"}`}>{ob.overdue ? `Form overdue ${ob.daysOver}d` : "Form pending"}</span>}
                      {c.agreement_signed
                        ? <span data-testid={`agreement-badge-${c.id}`} className="font-mono text-[9px] uppercase text-emerald-400 border border-emerald-800 px-1">Agreement signed ✓</span>
                        : <span data-testid={`agreement-badge-${c.id}`} className="font-mono text-[9px] uppercase text-yellow-400 border border-yellow-800 px-1">Agreement pending</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-[11px] font-mono text-zinc-500">
                  {(c.residence_zip || c.phone) && <div className="flex items-center gap-1.5"><MapPin size={11} className="text-zinc-600" /> {c.residence_zip || "—"}{c.phone ? ` · ${c.phone}` : ""}</div>}
                  {c.services?.length > 0 && (
                    <div className="flex items-start gap-1.5"><Wrench size={11} className="text-zinc-600 mt-0.5" /><span className="text-zinc-400">{c.services.slice(0, 4).join(", ")}{c.services.length > 4 ? ` +${c.services.length - 4}` : ""}</span></div>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-zinc-800 flex gap-2 flex-wrap">
                  <Btn variant="secondary" data-testid={`view-crew-${c.id}`} onClick={() => setViewId(c.id)} title="View profile"><Eye size={14} /></Btn>
                  <Btn variant="secondary" data-testid={`reset-crew-${c.id}`} onClick={() => sendReset(c)} title="Email a password reset link"><KeyRound size={14} /></Btn>
                  <Btn variant="secondary" data-testid={`convert-crew-${c.id}`} onClick={() => convertToClient(c)} title="Convert to Client"><UserCog size={14} /></Btn>
                  <Btn variant="secondary" data-testid={`edit-crew-${c.id}`} onClick={() => openEdit(c)} className="flex-1"><Pencil size={14} /> Edit</Btn>
                  <Btn variant={active ? "danger" : "secondary"} data-testid={`toggle-crew-${c.id}`} onClick={() => toggleActive(c)}>
                    {active ? <PowerOff size={14} /> : <Power size={14} />}
                  </Btn>
                  <Btn variant="ghost" data-testid={`map-crew-${c.id}`} onClick={() => toggleMap(c.id)} title="Show map"><MapPin size={14} /></Btn>
                  <Btn variant="danger" data-testid={`delete-crew-${c.id}`} onClick={() => setConfirmDel(c)}><Trash2 size={14} /></Btn>
                </div>
                {mapIds.includes(c.id) && <CrewMiniMap address={c.address || c.residence_zip} testid={`crew-map-${c.id}`} />}
              </Card>
            );
          })}
        </div>
      )}

      <LaunchEmailDialog open={launchOpen} onClose={() => setLaunchOpen(false)} />
      <CrewProfileDialog crewId={viewId} onClose={() => setViewId(null)} />

      {/* Import modal */}
      <Dialog open={importOpen} onOpenChange={(o) => !o && closeImport()}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head font-black uppercase flex items-center gap-2"><FileSpreadsheet size={18} className="text-[#FF5722]" /> Bulk Import Contractors</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Upload a <span className="text-white font-semibold">.csv or .xlsx</span> file to add or update contractors in bulk. Matched by <span className="text-white font-semibold">email</span> (no duplicates) — existing accounts are updated, new ones get an invite to <span className="text-white font-semibold">activate with Google</span>.</p>
            <div className="border border-zinc-800 p-3 bg-black/30">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-1">Recognized columns</div>
              <div className="font-mono text-xs text-zinc-300">Vendor/Company · Name · Email · Phone Number · Address · Residence ZIP · Service ZIPs · Counties</div>
              <button data-testid="download-template-btn" onClick={downloadTemplate} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF5722] hover:underline"><Download size={14} /> Download template (.csv)</button>
            </div>
            <div>
              <Label>Choose file</Label>
              <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="block w-full text-sm text-zinc-300 font-mono file:mr-3 file:py-2 file:px-3 file:border-2 file:border-black file:bg-[#FF5722] file:text-black file:font-bold file:uppercase file:text-xs file:cursor-pointer" />
              {file && <div className="mt-1 font-mono text-xs text-zinc-500 truncate">{file.name}</div>}
            </div>
            <Btn data-testid="run-import-btn" onClick={doImport} disabled={importing || !file} className="w-full">{importing ? "Importing…" : "Import Contractors"}</Btn>

            {result && (
              <div data-testid="import-result" className="space-y-2">
                <div className="flex flex-wrap gap-2 text-xs font-mono">
                  <span className="bg-green-900/40 border border-green-800 text-green-300 px-2 py-1">{result.created} added</span>
                  <span className="bg-blue-900/40 border border-blue-800 text-blue-300 px-2 py-1">{result.updated} updated</span>
                  <span className="bg-zinc-800 border border-black text-zinc-300 px-2 py-1">{result.invited} invites sent</span>
                  {result.skipped > 0 && <span className="bg-red-900/40 border border-red-800 text-red-300 px-2 py-1">{result.skipped} skipped</span>}
                </div>
                <div className="max-h-48 overflow-y-auto border border-zinc-800 divide-y divide-zinc-900">
                  {result.results.map((r, i) => (
                    <div key={`${r.email}-${i}`} className="flex items-start gap-2 px-2 py-1.5 text-xs">
                      {r.status === "skipped"
                        ? <AlertCircle size={13} className="text-red-400 mt-0.5 shrink-0" />
                        : <CheckCircle2 size={13} className="text-green-400 mt-0.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-white truncate">{r.name || "—"} <span className="text-zinc-500 font-mono">{r.email}</span></div>
                        <div className="font-mono text-[10px] uppercase text-zinc-500">{r.status}{r.reason ? ` · ${r.reason}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase">Add Contractor</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div><Label>Name</Label><Input data-testid="crew-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><Label>Email</Label><Input data-testid="crew-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><Label>Temporary Password</Label><Input data-testid="crew-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
            <Btn data-testid="crew-submit-btn" type="submit" className="w-full">Create Account</Btn>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head font-black uppercase">Edit Contractor</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="edit-crew-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
                <div><Label>Phone</Label><Input data-testid="edit-crew-phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
              </div>
              <div><Label>Email (login)</Label><Input data-testid="edit-crew-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="contractor@email.com" /></div>
              <div><Label>Company / Vendor</Label><Input data-testid="edit-crew-company" value={edit.company} onChange={(e) => setEdit({ ...edit, company: e.target.value })} /></div>
              <div><Label>Address</Label><Input data-testid="edit-crew-address" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></div>
              <CrewMiniMap address={edit.address} testid="edit-crew-map" />
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Residence ZIP</Label><Input data-testid="edit-crew-residence-zip" value={edit.residence_zip} onChange={(e) => setEdit({ ...edit, residence_zip: e.target.value })} /></div>
                <div><Label>Coverage radius</Label><Select data-testid="edit-crew-radius" value={edit.service_radius} onChange={(e) => setEdit({ ...edit, service_radius: Number(e.target.value) })}>{[20, 30, 40, 50, 60, 70, 80, 90, 100].map((r) => <option key={r} value={r}>{r} miles</option>)}</Select></div>
                <div><Label>State</Label><Select data-testid="edit-crew-state" value={edit.state || ""} onChange={(e) => setEdit({ ...edit, state: e.target.value })}><option value="">Auto from ZIP</option>{opStates.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}</Select></div>
              </div>
              <div><Label>Service ZIP codes (comma-separated)</Label><Input data-testid="edit-crew-service-zips" value={Array.isArray(edit.service_zips) ? edit.service_zips.join(", ") : edit.service_zips} onChange={(e) => setEdit({ ...edit, service_zips: e.target.value })} /></div>
              <div><Label>Counties (comma-separated)</Label><Input data-testid="edit-crew-counties" value={Array.isArray(edit.service_counties) ? edit.service_counties.join(", ") : edit.service_counties} onChange={(e) => setEdit({ ...edit, service_counties: e.target.value })} /></div>
              <div><Label>Services Offered</Label><ServiceChips selected={edit.services} onChange={(s) => setEdit({ ...edit, services: s })} options={CONTRACTOR_SERVICES} /></div>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="edit-crew-available" checked={edit.available} onChange={(e) => setEdit({ ...edit, available: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" /> Available for work</label>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="edit-crew-onboarded" checked={edit.onboarding_completed} onChange={(e) => setEdit({ ...edit, onboarding_completed: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" /> Onboarding form completed</label>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="edit-crew-agreement" checked={edit.agreement_signed} onChange={(e) => setEdit({ ...edit, agreement_signed: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" /> Subcontractor agreement signed</label>
              <div><Label>Admin Notes (private — not visible to contractor)</Label><Textarea data-testid="edit-crew-notes" value={edit.admin_notes} onChange={(e) => setEdit({ ...edit, admin_notes: e.target.value })} placeholder="Notes about this contractor and your interactions…" /></div>
              <div><Label>Public Bio (visible to contractor)</Label><Textarea data-testid="edit-crew-bio" value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} placeholder="Short bio about this contractor…" /></div>
              <div>
                <Label>Profile Photo</Label>
                <div className="flex items-center gap-3">
                  {(edit.picture_file_id || edit.picture) ? (
                    <img src={avatarUrl(edit)} alt="" className="w-14 h-14 object-cover border-2 border-black shadow-[2px_2px_0px_#000]" />
                  ) : (
                    <div className="w-14 h-14 bg-zinc-800 border-2 border-black flex items-center justify-center font-head font-black text-[#FF5722]">{edit.name?.[0]?.toUpperCase() || "?"}</div>
                  )}
                  <label className="inline-flex">
                    <input type="file" accept="image/*" className="hidden" data-testid="edit-crew-pic-input" onChange={uploadEditPic} disabled={uploadingPic} />
                    <span className={`inline-flex items-center gap-2 font-head font-bold uppercase text-xs px-3 py-2 border-2 border-zinc-700 bg-zinc-900 text-white cursor-pointer hover:border-[#FF5722] shadow-[2px_2px_0px_#000] ${uploadingPic ? "opacity-50 pointer-events-none" : ""}`}><Upload size={13} /> {uploadingPic ? "Uploading…" : "Upload"}</span>
                  </label>
                </div>
              </div>
              <div className="border-t border-zinc-800 pt-4">
                <DocumentManager basePath={`/crew/${edit.id}/documents`} title="Contractor Documents" hint="Insurance, price lists, counter-bids or contracts. The contractor can see these and upload their own." testid="crew-doc-manager" />
              </div>
              <Btn data-testid="save-crew-btn" onClick={saveEdit} className="w-full">Save Changes</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#141417] border-2 border-red-600 text-white rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase text-red-400">Delete contractor?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-300">Are you sure you want to permanently delete <span className="text-white font-semibold">{confirmDel?.name}</span>? They will be removed and unassigned from any jobs. This cannot be undone.</p>
          <div className="flex gap-2 justify-end mt-2">
            <Btn variant="secondary" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" data-testid="confirm-delete-crew" onClick={doDelete}>Yes, delete</Btn>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk email / broadcast modal */}
      <Dialog open={bcastOpen} onOpenChange={setBcastOpen}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0">
          <DialogHeader><DialogTitle className="font-head font-black uppercase flex items-center gap-2"><Megaphone size={18} className="text-[#FF5722]" /> Email Contractors</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Send news, updates or new-work announcements. Recipients get an in-app pop-up notification and (optionally) an email.</p>
            <div><Label>Subject</Label><Input data-testid="bcast-subject" value={bSubject} onChange={(e) => setBSubject(e.target.value)} placeholder="New work available in your area" /></div>
            <div><Label>Message</Label><Textarea data-testid="bcast-message" value={bMsg} onChange={(e) => setBMsg(e.target.value)} placeholder="Write your announcement…" className="min-h-[110px]" /></div>
            {bcastStates.length > 1 && (
              <div><Label>State (contractors in this state only)</Label>
                <Select data-testid="bcast-state" value={bState} onChange={(e) => { const s = e.target.value; setBState(s); setBSel(crew.filter((c) => s === "all" || c.state === s).map((c) => c.id)); }}>
                  <option value="all">All states</option>
                  {bcastStates.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
                {bState !== "all" && <p className="font-mono text-[10px] text-zinc-600 mt-1">Only contractors in {bState} are shown and will be notified.</p>}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Recipients ({bSel.length}/{bcastCrew.length})</Label>
                <button type="button" data-testid="bcast-select-all" onClick={() => setBSel(allSelected ? [] : bcastCrew.map((c) => c.id))} className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722] hover:underline">{allSelected ? "Clear all" : "Select all"}</button>
              </div>
              <div className="max-h-44 overflow-y-auto border border-zinc-800 divide-y divide-zinc-900">
                {bcastCrew.map((c) => (
                  <label key={c.id} data-testid={`bcast-pick-${c.id}`} className="flex items-center gap-2 px-2.5 py-2 text-sm hover:bg-zinc-900 cursor-pointer">
                    <input type="checkbox" checked={bSel.includes(c.id)} onChange={() => toggleSel(c.id)} className="w-4 h-4 accent-[#FF5722]" />
                    <span className="text-white truncate flex-1 min-w-0">{c.name}</span>
                    {c.state && <span className="font-mono text-[9px] text-zinc-400 border border-zinc-700 px-1">{c.state}</span>}
                    <span className="font-mono text-[10px] text-zinc-500 truncate min-w-0 max-w-[45%]">{c.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="bcast-send-email" checked={bEmail} onChange={(e) => setBEmail(e.target.checked)} className="w-4 h-4 accent-[#FF5722]" /> Also send as email</label>
            {bEmail && <div><Label>Attachments (pictures, videos, documents)</Label><MediaManager value={bMedia} onChange={setBMedia} makePublic label="Add attachments" testid="bcast-media" /><p className="font-mono text-[10px] text-zinc-600 mt-1">Images show inside the email; videos &amp; documents are sent as secure tap-to-view links.</p></div>}
            <Btn data-testid="bcast-send-btn" onClick={sendBcast} disabled={bSending} className="w-full"><Send size={16} /> {bSending ? "Sending…" : "Send Announcement"}</Btn>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={statesOpen} onOpenChange={setStatesOpen}>
        <DialogContent data-testid="states-dialog">
          <DialogHeader><DialogTitle className="font-head uppercase">Operating States</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              {opStates.map((s) => (
                <div key={s.code} data-testid={`state-row-${s.code}`} className="flex items-center gap-2 border border-zinc-800 px-3 py-2">
                  <span className="w-4 h-4 rounded-full" style={{ background: s.color }} />
                  <span className="text-white text-sm flex-1">{s.name} <span className="font-mono text-zinc-500">({s.code})</span></span>
                  <button data-testid={`del-state-${s.code}`} onClick={() => delState(s.code)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-800 pt-3 grid grid-cols-[70px_1fr_52px_auto] gap-2 items-end">
              <div><Label>Code</Label><Input data-testid="new-state-code" value={newState.code} onChange={(e) => setNewState({ ...newState, code: e.target.value.toUpperCase().slice(0, 2) })} placeholder="TX" /></div>
              <div><Label>Name</Label><Input data-testid="new-state-name" value={newState.name} onChange={(e) => setNewState({ ...newState, name: e.target.value })} placeholder="Texas" /></div>
              <div><Label>Color</Label><input type="color" data-testid="new-state-color" value={newState.color} onChange={(e) => setNewState({ ...newState, color: e.target.value })} className="w-full h-9 bg-transparent border-2 border-zinc-700" /></div>
              <Btn data-testid="add-state-btn" onClick={addState}><Plus size={14} /></Btn>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CrewMiniMap({ address, testid }) {
  if (!address) return <div className="mt-2 text-[11px] font-mono text-zinc-600 border border-dashed border-zinc-800 p-2 text-center">No address on file for this contractor.</div>;
  return (
    <iframe
      title="Contractor location"
      data-testid={testid}
      src={`https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=13&output=embed`}
      className="w-full mt-3 border border-zinc-800"
      style={{ height: 160, border: 0 }}
      loading="lazy"
    />
  );
}
